import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import Script from "next/script";
import { useRouter } from "next/router";
import type { AppProps } from "next/app";
import NextNProgress from "nextjs-progressbar";
import Hotjar from "@hotjar/browser";
import useGuestLog from "@/hooks/requests/useGuestLog";
import useHotjar from "@/hooks/plugins/useHotjar";
import { NextIntlClientProvider } from "next-intl";
import Cookies from "js-cookie";
import { SessionProvider } from "next-auth/react";
import type { Session } from "next-auth";
import { ThemeProvider, ThemeProviderProps } from 'next-themes';
import dynamic from "next/dynamic";
import App from "next/app";
import { merge } from "lodash";
import { useIANATimezoneDatabase } from "@/states/user.state";
import "rsuite/dist/rsuite.min.css";
import "@/assets/styles/_base.scss";
import "react-modern-drawer/dist/index.css";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import { useConsentMode } from "@/hooks/useConsentMode";
import GoogleAnalytics from '@/components/common/GoogleAnalytics';
import { Lato } from 'next/font/google';
const lato = Lato({
  subsets: ['latin'],
  weight: ['100', '300', '400', '700', '900'],
  display: 'swap',
  preload: true
});
type PageProps = {
  messages: any;
  now: number;
  status?: number;
  currentHost: string;
};
type Props = Omit<AppProps<PageProps>, "pageProps"> & {
  pageProps: PageProps & {
    session: Session;
  };
  messages: any;
};
const ErrorBoundary = dynamic(() => import("@/components/common/ErrorBoundary/ErrorBoundary"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: data => {
        return data;
      }
    }
  }
});
function MyApp({
  Component,
  pageProps,
  messages
}: Props) {
  const router = useRouter();
  const mergedMessages = merge({}, messages, pageProps.messages);
  const {
    setTimezone
  } = useIANATimezoneDatabase();
  const [isBot, setIsBot] = useState<boolean>(true);
  useGuestLog();
  useConsentMode();
  useHotjar(isBot);
  useEffect(() => {
    const isBotCookie = Cookies.get("is_bot");
    setIsBot(isBotCookie === "true");
    setTimezone();
  }, []);
  useEffect(() => {
    document.ontouchmove = function (event) {
      event.preventDefault();
    };
    if (!isBot && process.env.NEXT_PUBLIC_HOTJAR_ENABLED === "true" && process.env.NEXT_PUBLIC_HOTJAR_ID && process.env.NEXT_PUBLIC_HOTJAR_ID_QR_FAST) {
      const currentHost = window.location.host;
      const isQrCodeCreator = currentHost === process.env.NEXT_PUBLIC_SITE_HOST;
      if (isQrCodeCreator) {
        if (!router.pathname.includes("/preview-types")) {
          Hotjar.init(+process.env.NEXT_PUBLIC_HOTJAR_ID, 6);
        } else if (process.env.NEXT_PUBLIC_HOTJAR_QRIO_ID) {
          Hotjar.init(+process.env.NEXT_PUBLIC_HOTJAR_QRIO_ID, 6);
        }
      } else {
        Hotjar.init(+process.env.NEXT_PUBLIC_HOTJAR_ID_QR_FAST, 6);
      }
    }
  }, [isBot]);
  const renderSessionProvider = () => {
    const content = <div className={lato.className}>
        <style jsx global>{`
        html {
          font-family: ${lato.style.fontFamily};
        }
      `}</style>
        <Component {...pageProps} />
      </div>;
    const excludedPaths = ["/preview-types", "/auto-login", "/dashboard", "/checkout"];
    const isExcludedPath = excludedPaths.some(path => router.pathname.includes(path));
    return isExcludedPath ? content : <SessionProvider session={pageProps.session} data-sentry-element="SessionProvider" data-sentry-component="renderSessionProvider" data-sentry-source-file="_app.tsx">
          {content}
        </SessionProvider>;
  };
  const CustomThemeProvider = ThemeProvider as React.FC<React.PropsWithChildren<ThemeProviderProps>>;
  const theme = pageProps.currentHost === process.env.NEXT_PUBLIC_SITE_HOST ? "light" : pageProps.currentHost;
  return <div data-sentry-component="MyApp" data-sentry-source-file="_app.tsx">
      {/* Facebook Pixel */}
      {!isBot && <>
          <Script id="facebook_pixel" strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID}');
              fbq('track', 'PageView');
            `}
          </Script>
        </>}

      {/* Google Analytics */}
      <GoogleAnalytics data-sentry-element="GoogleAnalytics" data-sentry-source-file="_app.tsx" />

      {/* Next Progress */}
      <NextNProgress color="#4bb0b2" options={{
      showSpinner: false
    }} data-sentry-element="NextNProgress" data-sentry-source-file="_app.tsx" />

      {/* App Provider */}
      <NextIntlClientProvider locale={router.locale} messages={mergedMessages} timeZone="Europe/Vienna" data-sentry-element="NextIntlClientProvider" data-sentry-source-file="_app.tsx">
        <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
          <ErrorBoundary status={pageProps?.status} data-sentry-element="ErrorBoundary" data-sentry-source-file="_app.tsx">{<CustomThemeProvider defaultTheme={theme}>

              {/* Map */}
              <div id="map"></div>

              {/* Portal */}
              <div id="portal"></div>

              {/* Document */}
              {renderSessionProvider()}

            </CustomThemeProvider>}
          </ErrorBoundary>
        </QueryClientProvider>
      </NextIntlClientProvider>
    </div>;
}
MyApp.getInitialProps = async (appContext: any) => {
  const appProps = await App.getInitialProps(appContext);
  const messages = (await import(`@/messages/qrcodecreator/${appContext.ctx.locale || "en"}/_shared.json`)).default;
  const currentHost = appContext.ctx.req?.headers["x-current-host"] || process.env.NEXT_PUBLIC_SITE_HOST;
  return {
    ...appProps,
    messages,
    pageProps: {
      ...appProps.pageProps,
      currentHost
    }
  };
};
export default MyApp;